import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, CircularProgress } from '@mui/material';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FlightIcon from '@mui/icons-material/Flight';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import NearMeIcon from '@mui/icons-material/NearMe';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface ContactInfoContent {
  title: string;
  description: string;
  email: string;
  phone: string;
  address: string;
  hours: string;
  additionalInfo: string;
}

const phoneRows = [
  { label: 'Temple Office', value: '0496 2690751' },
  { label: 'Pooja Booking', value: '0496 2690751' },
];

const emailRows = [
  { label: 'General Queries', value: '' },
  { label: 'Pooja Booking', value: 'poojas@muchukunnutemple.org' },
  { label: 'Donations', value: 'donations@muchukunnutemple.org' },
  { label: 'Feedback', value: 'feedback@muchukunnutemple.org' },
];

const howToReach = [
  { Icon: FlightIcon, mode: 'By Air', detail: 'Calicut International Airport (45 km)', gradient: ['#3b82f6', '#2563eb'] },
  { Icon: TrainIcon, mode: 'By Train', detail: 'Koyilandy Railway Station (8 km)', gradient: ['#22c55e', '#16a34a'] },
  { Icon: DirectionsBusIcon, mode: 'By Road', detail: 'Well connected by State & Private buses', gradient: ['#f59e0b', '#d97706'] },
];

export default function ContactInfo() {
  const [content, setContent] = useState<ContactInfoContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const docRef = doc(db, 'contact_content', 'info');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setContent(docSnap.data() as ContactInfoContent);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#06b6d4' }} />
      </Box>
    );
  }

  if (!content) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">Unable to load contact information</Typography>
      </Box>
    );
  }

  const phones = phoneRows.map((p) => ({ ...p, value: p.value || content.phone }));
  const emails = emailRows.map((e) => ({ ...e, value: e.value || content.email }));

  const infoCards: { Icon: typeof PhoneIcon; title: string; gradient: [string, string]; content: React.ReactNode }[] = [
    {
      Icon: PhoneIcon, title: 'Phone Numbers', gradient: ['#3b82f6', '#2563eb'],
      content: phones.map((p, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.7, borderBottom: i < phones.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{p.label}</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#3b82f6' }}>{p.value}</Typography>
        </Box>
      )),
    },
    {
      Icon: EmailIcon, title: 'Email Addresses', gradient: ['#22c55e', '#16a34a'],
      content: emails.map((e, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.7, borderBottom: i < emails.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none', flexWrap: 'wrap', gap: 0.3 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{e.label}</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#22c55e' }}>{e.value}</Typography>
        </Box>
      )),
    },
    {
      Icon: LocationOnIcon, title: 'Address', gradient: ['#f59e0b', '#d97706'],
      content: (
        <>
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#f59e0b', mb: 0.3 }}>Kottayil Temple</Typography>
          <Typography sx={{ fontSize: '0.76rem', color: '#94a3b8', lineHeight: 1.8, mb: 1.2 }}>Muchukunnu Akalappuzha Road, Moodadi, Kerala 673307</Typography>
          <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.06)', my: 1 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#f59e0b', mb: 0.3 }}>Kovilakam Temple</Typography>
          <Typography sx={{ fontSize: '0.76rem', color: '#94a3b8', lineHeight: 1.8 }}>Muchukunnu Akalappuzha Road, Moodadi, Kerala 673307</Typography>
        </>
      ),
    },
    {
      Icon: AccessTimeIcon, title: 'Office Hours', gradient: ['#8b5cf6', '#7c3aed'],
      content: (
        <>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mb: 1 }}>Monday to Sunday</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#8b5cf6', mb: 0.5 }}>Kottayil Temple</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Morning</Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>6:00 AM – 11:00 AM</Typography>
          </Box>
          <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.06)', my: 1 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#8b5cf6', mb: 0.5 }}>Kovilakam Temple</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Morning</Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>5:30 AM – 08:30 AM</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Evening</Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>5:30 PM – 07:30 PM</Typography>
          </Box>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Contact Information — Kottayil Temple | സംപർക്ക വിവരങ്ങൾ" description="Phone numbers, email, address, and visiting hours for Kottayil Temple (Kotta-Kovilakam Kshethram) at Muchukunnu, Kozhikode." path="/contact/info" keywords="Kottayil temple phone number, temple address, visiting hours, contact details, കൊട്ടയിൽ ക്ഷേത്രം ഫോൺ, സന്ദർശന സമയം" />
      <PageBanner title="Contact Information" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #164e63 0%, #0e7490 40%, #06b6d4 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <ContactPhoneIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Contact Information
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      All contact details for the temple office and departments
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  {content.description || 'Reach out to us by phone, email, or visit the temple office during working hours.'}
                </Typography>

              </Box>
            </Box>

            {/* INFO CARDS */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {infoCards.map((card) => {
                const CIcon = card.Icon;
                return (
                  <Grid item xs={12} sm={6} key={card.title}>
                    <Box sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                      height: '100%',
                      transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 20px 60px rgba(0,0,0,0.1)` },
                    }}>
                      <Box sx={{ background: `linear-gradient(135deg, ${card.gradient[0]}, ${card.gradient[1]})`, px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                          <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                            <CIcon sx={{ fontSize: 22, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>{card.title}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ p: 2.5 }}>{card.content}</Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* HOW TO REACH FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <NearMeIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>How to Reach</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Travel options to the temple</Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  {howToReach.map((h, i) => {
                    const TIcon = h.Icon;
                    return (
                      <Grid item xs={12} sm={4} key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5, borderRadius: '14px', transition: 'all 250ms', '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${h.gradient[0]}, ${h.gradient[1]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 16px ${h.gradient[0]}30`, flexShrink: 0 }}>
                            <TIcon sx={{ fontSize: 20, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: 'rgba(255,255,255,0.75)' }}>{h.mode}</Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>{h.detail}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="contact" activePath="/contact/info" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
